import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import SettingsOutlinedIcon from '@guildeducationinc/recess/icons/SettingsOutlinedIcon';
import { Pagination, Tooltip } from '@mui/material';
import { DEFAULT_FIELDS } from '@src/components/Admin/Employees/Employees';
import { FieldSelectionModal } from '@src/components/Admin/FieldSelectionModal';
import { useEmployeeSnapshots } from '@src/hooks/useEmployeeSnapshots';
import { EntityType } from '@src/types';
import classNames from 'classnames';
import { DatePicker, TrackableButton, useFields, useLocale, usePreferenceState, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
export const EmployeeSnapshots = ({ clientId, employeeId }) => {
    const { t } = useLocale();
    const [showCustomize, setShowCustomize] = useState(false);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const { snapshots, loading, page, pageSize, setPage, total } = useEmployeeSnapshots({
        clientId,
        employeeId,
        startDate,
        endDate,
    });
    const numPages = Math.ceil(total / pageSize);
    const { items: metadata } = useFields({
        clientId,
        entityType: EntityType.EMPLOYEE,
    });
    const [selectedFields, setSelectedFields] = usePreferenceState(`employeeSnapshotColumnsToDisplay_${clientId}`, DEFAULT_FIELDS);
    const gridColumns = useMemo(() => {
        const columns = [
            {
                field: 'id',
                width: 120,
                headerName: t('Snapshot Date'),
            },
        ];
        if (!metadata)
            return columns;
        for (const fieldInfo of metadata) {
            if (selectedFields.includes(fieldInfo.code)) {
                if (fieldInfo.isActive) {
                    columns.push({
                        field: fieldInfo.code,
                        // flex: 0.1,
                        headerName: fieldInfo.name,
                        sortable: false,
                        renderCell: ({ row }) => {
                            return row.employeeData[fieldInfo.code];
                        },
                    });
                }
            }
        }
        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata, selectedFields]);
    return (_jsxs("div", { children: [_jsxs("div", { className: 'flex justify-between mb-2 items-center', children: [_jsxs("div", { className: 'flex gap-4', children: [_jsx(DatePicker, { label: t('Start Date'), value: startDate || '', onChange: setStartDate }), _jsx(DatePicker, { label: t('End Date'), value: endDate || '', onChange: setEndDate })] }), _jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Customize Fields', tag: 'employee_snapshots', appearance: 'minimal', label: t('Customize Fields'), icon: _jsx(SettingsOutlinedIcon, { size: 24 }), onClick: () => setShowCustomize(true) }) })] }), _jsx("div", { className: 'overflow-x-auto shadow-inner p-1', children: _jsxs("table", { className: 'border-collapse border border-slate-500 w-full text-sm', children: [_jsx("thead", { children: _jsx("tr", { children: gridColumns.map(col => (_jsx("th", { className: 'border border-slate-400 p-1', children: col.headerName }, col.field))) }) }), _jsx("tbody", { children: snapshots.map(snapshot => (_jsx("tr", { children: gridColumns.map((col, idx) => (_jsx("td", { className: 'border border-slate-400 p-1 text-nowrap', children: _jsx(Tooltip, { title: idx !== 0 || snapshot.id === snapshot.snapshotDate
                                            ? undefined
                                            : t('Snapshot missing. Using data from {date}', {
                                                values: { date: snapshot.snapshotDate },
                                            }), children: idx === 0 ? (_jsx("span", { className: classNames(snapshot.id !== snapshot.snapshotDate && 'text-red'), children: snapshot.id })) : (_jsx("span", { children: snapshot.employeeData[col.field] })) }) }, col.field))) }, snapshot.id))) })] }) }), !loading && numPages > 1 && (_jsx("div", { className: 'flex justify-center mt-2', children: _jsx(Pagination, { color: 'primary', count: numPages, page: page, onChange: (_, page) => setPage(page) }) })), showCustomize && (_jsx(FieldSelectionModal, { fields: metadata || [], selectedFields: selectedFields, onChange: setSelectedFields, onClose: () => setShowCustomize(false) }))] }));
};
